
import { useCallback, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import EditorTopbar from "./EditorTopbar";
import UploadFile from "./viewer/UploadFile";
import PdfViewer from "./viewer/PdfViewer";
import {getJobResult} from "../api/jobRoutes";
import {JOB_STATUS} from "../constants/status";
import DocumentInspector from "./content/DocumentInspector";

export default function EditorPage() {
    const { jobId } = useParams();

    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);

    const [leftWidth, setLeftWidth] = useState(45);
    const [isDragging, setIsDragging] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hoveredChunkId, setHoveredChunkId] = useState(null);

    const leftPanelRef = useRef(null);
    const rafRef = useRef(null);
    const barRef = useRef(null);

    const fetchJob = useCallback(async () => {
        try {
            setLoading(true);

            const response = await getJobResult(jobId)

            setJob(response);

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        if (!jobId) return;
        fetchJob();
    }, [jobId, fetchJob]);




    const draggingRef = useRef(false);
    const latestWidthRef = useRef(leftWidth);



    const handlePointerMove = (e) => {
        if (!draggingRef.current) return;

        const newWidth = (e.clientX / window.innerWidth) * 100;
        const clamped = Math.min(80, Math.max(35, newWidth));
        latestWidthRef.current = clamped;

        if (rafRef.current) return;
        rafRef.current = requestAnimationFrame(() => {
            if (leftPanelRef.current) {
                leftPanelRef.current.style.width = `${latestWidthRef.current}%`;
            }
            rafRef.current = null;
        });
    };

    const handlePointerDown = (e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        draggingRef.current = true;
        latestWidthRef.current = leftWidth;
        barRef.current.dataset.dragging = "true";
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    };

    const endDrag = (e) => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        barRef.current.dataset.dragging = "false";
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        setLeftWidth(latestWidthRef.current);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "100vh",
                width: "100%",
                display: "flex",
                overflow: "hidden",
                background: "#f4f4f5",
            }}
        >
            {/* Left side */}
            <Box
                ref={leftPanelRef}
                sx={{
                    width: `${leftWidth}%`,
                    display: "flex",
                    flexDirection: "column",
                    borderRight: "1px solid #e4e4e7",
                    background: "#f8f8f8",
                    
                }}
            >
                <EditorTopbar job={job} fetchJob={fetchJob} />

                {/* Conditional content */}
                {job?.status === JOB_STATUS.PENDING ? (
                    <UploadFile
                        job={job}
                        onUploadSuccess={() => {
                            fetchJob();
                        }}
                    />
                ) : (
                    <PdfViewer
                        pages={job?.file?.pages}
                        jobType={job?.type}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        hoveredChunkId={hoveredChunkId}
                        setHoveredChunkId={setHoveredChunkId}
                    />
                )}
            </Box>

            {/* Resize bar */}
            <Box
                ref={barRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                sx={{
                    width: 6,
                    cursor: "col-resize",
                    transition: "background .2s",
                    position: "relative",
                    touchAction: "none",
                    zIndex: 20,

                    "&:hover, &[data-dragging='true']": { background: "#7c3aed" },
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform:
                            "translate(-50%, -50%)",
                        width: 2,
                        height: 80,
                        borderRadius: 99,
                        background:
                            "rgba(124,58,237,.3)",
                    }}
                />
            </Box>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    background: "white",
                }}
            >
                <DocumentInspector job={job} currentPage={currentPage} hoveredChunkId={hoveredChunkId}
                                   setHoveredChunkId={setHoveredChunkId}
                                   leftWidth={leftWidth}
                />
            </Box>
        </Box>
    );
}